import {Attributes, Button, Card, Flags, Label, openDialog} from 'platform/components';
import {Right, Show, Text, VStack} from 'platform/foundation';

import {indexBy} from 'ramda';
import {isFalse, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetAiLeadSettingsQuery, useUpdateAiLeadSettingsMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {AiLeadCard} from './components/AiLeadCard';
import {AiLeadConfigurationForm} from './components/AiLeadConfigurationForm';

export function AiLeadManagement() {
  const {data: aiLeadSettings, isLoading, isError} = useGetAiLeadSettingsQuery();
  const [updateAiLeadSettings, {isLoading: isUpdating}] = useUpdateAiLeadSettingsMutation();
  const {data: branches} = useBranches();
  const branchesById = indexBy((branch) => branch.id, branches?.branchListItems ?? []);

  const handleUpdateAiLeadSettings = () => {
    openDialog(
      <AiLeadConfigurationForm
        data-testid={testIds.settings.aiLeadManagement('leadConfigurationForm')}
      />,
      {
        title: i18n.t('page.settings.labels.aiLeadManagement'),
      }
    );
  };

  const handleToggle = (value: boolean) => {
    if (isFalse(value)) {
      return updateAiLeadSettings({
        enabled: false,
        allowedEmailAddresses: [],
        defaultBranchId: null,
      })
        .unwrap()
        .catch(handleApiError);
    }

    handleUpdateAiLeadSettings();
  };

  return (
    <SettingsTemplate
      header={{title: i18n.t('page.settings.labels.aiLeadManagement')}}
      data-testid="ai-lead-management-settings"
      isLoading={isLoading}
      isError={isError}
    >
      <SettingsSection>
        <VStack spacing={6}>
          <AiLeadCard data-testid={testIds.settings.aiLeadManagement('leadCard')} />

          <Card
            control={{
              type: 'switch',
              value: aiLeadSettings?.enabled ?? false,
              onChange: handleToggle,
              isLoading: isUpdating,
            }}
            title={i18n.t('page.settings.labels.aiLeadManagementEnabled')}
            isExpanded={aiLeadSettings?.enabled}
            variant="inlineWhite"
            data-testid={testIds.settings.aiLeadManagement('enabledCard')}
          >
            <VStack spacing={4}>
              <Text
                size="small"
                color="secondary"
                data-testid={testIds.settings.aiLeadManagement('enabledDescription')}
              >
                {i18n.t('page.settings.labels.aiLeadManagementEnabledDescription')}
              </Text>

              <Attributes
                data-testid={testIds.settings.aiLeadManagement('settingsAttributes')}
                size="third"
                rows={[
                  {
                    label: i18n.t('entity.branch.labels.defaultBranch'),
                    value: branchesById[aiLeadSettings?.defaultBranchId ?? '']?.marketingName,
                  },
                  {
                    label: i18n.t('page.settings.labels.ProcessingEmailAddress'),
                    value: aiLeadSettings?.processingEmailAddress,
                  },
                ]}
              />

              <Show when={isNotNilOrEmpty(aiLeadSettings?.allowedEmailAddresses)}>
                <VStack>
                  <Label data-testid={testIds.settings.aiLeadManagement('allowedEmailsLabel')}>
                    {i18n.t('page.settings.labels.AllowedEmailAddresses')}
                  </Label>
                  <Flags
                    shouldWrap
                    flags={
                      aiLeadSettings?.allowedEmailAddresses?.map((item) => ({
                        label: item,
                        colorScheme: 'blue',
                        isSubtle: true,
                      })) ?? []
                    }
                    data-testid={testIds.settings.aiLeadManagement('allowedEmailsFlags')}
                  />
                </VStack>
              </Show>

              <Right>
                <Button
                  title={i18n.t('general.actions.edit')}
                  onClick={handleUpdateAiLeadSettings}
                  data-testid={testIds.settings.aiLeadManagement('editButton')}
                />
              </Right>
            </VStack>
          </Card>
        </VStack>
      </SettingsSection>
    </SettingsTemplate>
  );
}
